import request from '../client/request'

/**
 * Creates a product API service with methods to interact with product-related endpoints.
 *
 * @param {string} baseUrl - The base URL of the API.
 * @param {string} endpoint - The specific endpoint for product-related operations.
 * @returns {Object} An object containing methods for product API operations.
 * @property {Function} updateProductStatusById - Updates the status of a product by its ID.
 * @property {Function} updateProductImagePosition - Updates the position of a product's image.
 *
 * @typedef {Object} UpdateProductStatusParams
 * @property {string} id - The ID of the product.
 * @property {string} status - The new status of the product.
 *
 * @typedef {Object} UpdateProductImagePositionParams
 * @property {string} productId - The ID of the product.
 * @property {string} imageId - The ID of the image.
 * @property {number} position - The new position of the image.
 *
 * @function updateProductStatusById
 * @param {UpdateProductStatusParams} params - The parameters for updating the product status.
 * @returns {Promise<Object>} A promise that resolves to the response data.
 *
 * @function updateProductImagePosition
 * @param {UpdateProductImagePositionParams} params - The parameters for updating the image position.
 * @returns {Promise<Object>} A promise that resolves to the response data.
 */
const productApiService = (baseUrl, endpoint) => ({
  updateProductStatusById: async ({ id, status, rejectionReason }) => {
    const body = JSON.stringify({
      id,
      status,
      ...(rejectionReason && { rejectionReason })
    })
    const response = await request(`${baseUrl}${endpoint}/status`, {
      method: 'POST',
      data: body
    })
    const data = response.data
    return data
  },
  updateProductImagePosition: async ({ productId, imageId, position }) => {
    const body = JSON.stringify({
      productId,
      imageId,
      position
    })
    const response = await request(
      `${baseUrl}${endpoint}/media/position`,
      {
        method: 'POST',
        data: body
      }
    )
    const data = response.data
    return data
  }
})

export default productApiService(
  process.env.REACT_APP_API_PRODUCT_PATH,
  '/product/update'
)
